'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@client/ui/components/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@client/ui/components/card.tsx';
import { Input } from '@client/ui/components/input.tsx';
import { Label } from '@client/ui/components/label.tsx';
import { Separator } from '@client/ui/components/separator.tsx';
import { useAuthActions } from '@platform/nextjs/contexts/auth/use-auth-actions.ts';
import { loginSchema, type LoginFormData } from '@/lib/validations/auth.ts';

const MAGIC_LINK_COOLDOWN_SECONDS = 30;

function getSafeReturnPath(returnUrl: string | null): string {
  if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
    return '/sec';
  }

  return returnUrl;
}

export default function LoginPage() {
  const searchParams = useSearchParams();
  const { signIn, sendMagicLink } = useAuthActions();
  const [error, setError] = useState<string | null>(null);
  const [magicLinkNotice, setMagicLinkNotice] = useState<string | null>(null);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [magicLinkCooldownSeconds, setMagicLinkCooldownSeconds] = useState(0);

  // Get return URL from query params, default to /sec
  const returnUrl = getSafeReturnPath(searchParams.get('returnUrl'));

  const {
    register,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    if (magicLinkCooldownSeconds <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setMagicLinkCooldownSeconds((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [magicLinkCooldownSeconds]);

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    setMagicLinkNotice(null);
    const result = await signIn(data.email, data.password);
    if (result.error) {
      setError(result.error.message);
    } else {
      window.location.assign(returnUrl);
    }
  };

  const handleSendMagicLink = async () => {
    setError(null);
    setMagicLinkNotice(null);

    const isEmailValid = await trigger('email');
    if (!isEmailValid) {
      return;
    }

    setIsSendingMagicLink(true);

    try {
      const email = getValues('email');
      const magicLinkRedirectTo =
        `${window.location.origin}/auth/callback?next=` +
        encodeURIComponent(returnUrl);
      const result = await sendMagicLink(email, magicLinkRedirectTo);

      if (result.error) {
        setError(result.error.message);
        return;
      }

      setMagicLinkNotice(`Magic link sent to ${email}. Check your inbox.`);
      setMagicLinkCooldownSeconds(MAGIC_LINK_COOLDOWN_SECONDS);
    } finally {
      setIsSendingMagicLink(false);
    }
  };

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">Welcome back</CardTitle>
        <CardDescription>
          Enter your credentials to access your account
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {magicLinkNotice && (
            <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-700 dark:bg-blue-950/20 dark:text-blue-300">
              {magicLinkNotice}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              disabled={isSubmitting}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                href="/forgot"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              disabled={isSubmitting}
              {...register('password')}
            />
            {errors.password && (
              <p className="text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || isSendingMagicLink}
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </Button>
          <div className="flex w-full items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Or
            </span>
            <Separator className="flex-1" />
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={
              isSubmitting ||
              isSendingMagicLink ||
              magicLinkCooldownSeconds > 0
            }
            onClick={handleSendMagicLink}
          >
            {isSendingMagicLink
              ? 'Sending magic link...'
              : magicLinkCooldownSeconds > 0
                ? `Resend in ${magicLinkCooldownSeconds}s`
                : 'Email me a magic link'}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link href="/signup" className="text-primary hover:underline">
              Sign up
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
